/**
 * Perfil — o que é da CONTA, não da viagem.
 *
 * Nome e e-mail, aparência (tema), senha e sair. Chega-se pelo avatar do
 * cabeçalho. O que é da viagem (financeiro, configurações, grupo) continua
 * em Mais: misturar os dois faria a pessoa procurar a moeda da viagem no
 * lugar onde troca a própria senha.
 *
 * Trocar a senha pede a atual e derruba as outras sessões — não esta.
 */
import type { ThemePreference } from '@nv/tokens';
import { useState, type FormEvent, useRef } from 'react';

import { ApiError, changePassword } from '../../auth/api';
import { useAuthStore } from '../../auth/store';
import { useTheme } from '../../theme/ThemeProvider';
import { NascimentoNoPerfil } from '../aniversarios/NascimentoNoPerfil';
import { EmergenciaNoPerfil } from '../emergencia/EmergenciaNoPerfil';
import { RecorteDeImagem } from '../../ui/RecorteDeImagem';
import { FotoAvatar, useMeuAvatar, useTrocarAvatar } from './FotoAvatar';
import { Icon } from '../../ui/icons';
import { Card, Row, ScreenHeader, SectionLabel } from '../../ui/primitives';
import { AlertasNesteAparelho } from '../alertas/AlertasNesteAparelho';
import { NovidadesDoGrupo } from '../alertas/NovidadesDoGrupo';
import { Field } from '../auth/Field';
import { Sheet } from '../../ui/Sheet';

const MINIMO_SENHA = 8;

const TEMAS: { valor: ThemePreference; rotulo: string }[] = [
  { valor: 'system', rotulo: 'Sistema' },
  { valor: 'light', rotulo: 'Claro' },
  { valor: 'dark', rotulo: 'Escuro' },
];

export function PerfilScreen() {
  const user = useAuthStore((s) => s.user);
  const signOut = useAuthStore((s) => s.signOut);
  const { preference, setPreference } = useTheme();

  const [trocandoSenha, setTrocandoSenha] = useState(false);
  // Qual folha do Perfil está aberta (uma por vez).
  const [aberta, setAberta] = useState<'aniversario' | 'emergencia' | 'alertas' | 'senha' | null>(null);
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const novaCurta = nova.length > 0 && nova.length < MINIMO_SENHA;
  const naoConfere = confirmacao.length > 0 && confirmacao !== nova;
  const podeTrocar = atual.length > 0 && nova.length >= MINIMO_SENHA && confirmacao === nova;

  async function trocar(evento: FormEvent) {
    evento.preventDefault();
    if (!podeTrocar) return;
    setEnviando(true);
    setErro(null);
    setAviso(null);
    try {
      await changePassword(atual, nova);
      setAviso('Senha alterada. As outras sessões desta conta foram encerradas.');
      setTrocandoSenha(false);
      setAberta(null);
      setAtual('');
      setNova('');
      setConfirmacao('');
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível alterar a senha agora.');
    } finally {
      setEnviando(false);
    }
  }

  const meuAvatar = useMeuAvatar();
  const avatar = useTrocarAvatar();
  const arquivoDaFoto = useRef<HTMLInputElement>(null);
  const cameraDaFoto = useRef<HTMLInputElement>(null);
  const [escolhendoFoto, setEscolhendoFoto] = useState(false);
  const [previa, setPrevia] = useState<{ arquivo: File; url: string } | null>(null);

  function escolher(arquivo: File | undefined) {
    if (arquivoDaFoto.current) arquivoDaFoto.current.value = '';
    if (cameraDaFoto.current) cameraDaFoto.current.value = '';
    if (!arquivo) return;
    setPrevia({ arquivo, url: URL.createObjectURL(arquivo) });
  }
  function descartarPrevia(voltarAEscolher = false) {
    if (previa) URL.revokeObjectURL(previa.url);
    setPrevia(null);
    setEscolhendoFoto(voltarAEscolher);
  }
  async function confirmarPrevia(recorte: Blob) {
    if (!previa) return;
    // O recorte (item 15) já é o quadrado que a pessoa escolheu; sobe como foto.
    const arquivo = new File([recorte], 'avatar.jpg', { type: 'image/jpeg' });
    try {
      await avatar.trocar.mutateAsync(arquivo);
      descartarPrevia(false);
    } catch {
      // O erro aparece na tela, abaixo do nome; o recorte fica para tentar de novo.
    }
  }

  return (
    <section className="screen perfil">
      <ScreenHeader title="Perfil" />

      {/* Refino final: avatar de 80 com selo de câmera, nome 24/900 e e-mail. */}
      <div className="perfil__identidade">
        <button type="button" className="perfil__foto-botao" onClick={() => setEscolhendoFoto(true)} aria-label="Trocar a foto" disabled={avatar.trocar.isPending}>
          <FotoAvatar nome={user?.name ?? '?'} url={meuAvatar.data?.url} tamanho={80} className="perfil__avatar" />
          <span className="perfil__foto-lapis" aria-hidden="true"><Icon name="memorias" size={14} /></span>
        </button>
        <div className="perfil__linhas">
          <p className="perfil__nome">{user?.name ?? '—'}</p>
          <p className="perfil__email">{user?.email ?? ''}</p>
          <button type="button" className="perfil__trocar-foto" onClick={() => setEscolhendoFoto(true)} disabled={avatar.trocar.isPending} data-testid="trocar-foto">
            {avatar.trocar.isPending ? 'Enviando…' : meuAvatar.data?.url ? 'Trocar foto' : 'Pôr uma foto'}
          </button>
          {avatar.trocar.isError && <p className="auth-error" role="alert">Não foi possível mudar a foto agora.</p>}
        </div>
        {/* Dois inputs: `capture` abre a câmera no celular (no computador,
            o seletor comum). A foto escolhida passa pela pré-visualização
            antes de subir — ninguém troca a foto sem ver. */}
        <input ref={arquivoDaFoto} type="file" accept="image/*" hidden data-testid="arquivo-da-foto" onChange={(e) => escolher(e.target.files?.[0])} />
        <input ref={cameraDaFoto} type="file" accept="image/*" capture="user" hidden data-testid="camera-da-foto" onChange={(e) => escolher(e.target.files?.[0])} />
      </div>

      {escolhendoFoto && !previa && (
        <Sheet titleId="perfil-foto-titulo" onFechar={() => setEscolhendoFoto(false)}>
            <h2 className="sheet__title" id="perfil-foto-titulo">{meuAvatar.data?.url ? 'Trocar foto' : 'Pôr uma foto'}</h2>
            <p className="sheet__text">Quem divide uma viagem com você vê a foto. Ela é reduzida no aparelho antes de subir.</p>
            <ul className="nav-apps">
              <li>
                <button type="button" className="nav-apps__item" onClick={() => cameraDaFoto.current?.click()} data-testid="foto-tirar">
                  <span className="nav-apps__icone" aria-hidden="true"><Icon name="memorias" size={20} /></span>
                  <span className="nav-apps__nome">Tirar foto</span>
                </button>
              </li>
              <li>
                <button type="button" className="nav-apps__item" onClick={() => arquivoDaFoto.current?.click()} data-testid="foto-galeria">
                  <span className="nav-apps__icone" aria-hidden="true"><Icon name="documento" size={20} /></span>
                  <span className="nav-apps__nome">Escolher da galeria</span>
                </button>
              </li>
            </ul>
            <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={() => setEscolhendoFoto(false)}>Cancelar</button>
        </Sheet>
      )}

      {previa && (
        <RecorteDeImagem
          arquivo={previa.arquivo}
          onCancelar={() => descartarPrevia(true)}
          ocupado={avatar.trocar.isPending}
          onConfirmar={(recorte) => void confirmarPrevia(recorte)}
        />
      )}

      {/* Aparência: três miniaturas; trocar aplica o tema na hora. */}
      <SectionLabel>Aparência</SectionLabel>
      <div className="perfil__temas" role="radiogroup" aria-label="Tema">
        {TEMAS.map((t) => (
          <button
            type="button"
            key={t.valor}
            role="radio"
            aria-checked={preference === t.valor}
            className={preference === t.valor ? 'perfil__tema perfil__tema--ativo' : 'perfil__tema'}
            onClick={() => setPreference(t.valor)}
          >
            <span className={`perfil__tema-previa perfil__tema-previa--${t.valor}`} aria-hidden="true">
              <span className="perfil__tema-lado perfil__tema-lado--a"><i /><b /><b /></span>
              <span className="perfil__tema-lado perfil__tema-lado--b"><i /><b /></span>
            </span>
            {t.rotulo}
          </button>
        ))}
      </div>
      <p className="auth-note perfil__tema-nota">
        {preference === 'system'
          ? 'Acompanha o modo claro ou escuro do aparelho.'
          : 'Fixo neste aparelho, independente do modo do sistema.'}
      </p>

      {aviso && (
        <p className="auth-note" role="status">
          {aviso}
        </p>
      )}

      {/* Os formulários que ficavam abertos na página viram linhas que abrem
          folhas (refino final). O conteúdo de cada folha é o mesmo de antes. */}
      <Card lista className="perfil__lista">
        <Row avatar={<span className="perfil__linha-icone perfil__linha-icone--aniv" aria-hidden="true"><Icon name="bolo" size={18} /></span>} title="Aniversário" detail="Só o dia e o mês aparecem para o grupo" onClick={() => setAberta('aniversario')} data-testid="perfil-aniversario" />
        <Row avatar={<span className="perfil__linha-icone perfil__linha-icone--emg" aria-hidden="true"><Icon name="aviso" size={18} /></span>} title="Emergência" detail="Telefone, contato, saúde e seguro" onClick={() => setAberta('emergencia')} data-testid="perfil-emergencia" />
        <Row avatar={<span className="perfil__linha-icone perfil__linha-icone--alertas" aria-hidden="true"><Icon name="notificacoes" size={18} /></span>} title="Alertas" detail="Neste aparelho e novidades do grupo" onClick={() => setAberta('alertas')} data-testid="perfil-alertas" />
        <Row avatar={<span className="perfil__linha-icone perfil__linha-icone--senha" aria-hidden="true"><Icon name="semPermissao" size={18} /></span>} title="Senha" detail="Alterar senha" onClick={() => { setAberta('senha'); setTrocandoSenha(true); }} data-testid="perfil-senha" />
      </Card>

      <button type="button" className="perfil__sair" onClick={() => void signOut()}>
        <Icon name="sair" size={18} />
        Sair da conta
      </button>

      {aberta === 'aniversario' && (
        <Sheet titleId="perfil-aniv" onFechar={() => setAberta(null)}>
          <h2 className="sheet__title" id="perfil-aniv">Aniversário</h2>
          <NascimentoNoPerfil />
        </Sheet>
      )}
      {aberta === 'emergencia' && (
        <Sheet titleId="perfil-emg" onFechar={() => setAberta(null)}>
          <h2 className="sheet__title" id="perfil-emg">Emergência</h2>
          <EmergenciaNoPerfil />
        </Sheet>
      )}
      {aberta === 'alertas' && (
        <Sheet titleId="perfil-alertas" onFechar={() => setAberta(null)}>
          <h2 className="sheet__title" id="perfil-alertas">Alertas</h2>
          <AlertasNesteAparelho />
          <NovidadesDoGrupo />
        </Sheet>
      )}
      {aberta === 'senha' && (
        <Sheet titleId="perfil-senha" onFechar={() => { setAberta(null); setTrocandoSenha(false); setErro(null); }}>
          <h2 className="sheet__title" id="perfil-senha">Alterar senha</h2>
          <form className="auth-form" onSubmit={trocar}>
            <Field
              label="Senha atual"
              type="password"
              autoComplete="current-password"
              value={atual}
              onChange={(e) => setAtual(e.target.value)}
              required
            />
            <Field
              label="Nova senha"
              type="password"
              autoComplete="new-password"
              value={nova}
              onChange={(e) => setNova(e.target.value)}
              required
              error={novaCurta ? `Use pelo menos ${MINIMO_SENHA} caracteres.` : undefined}
            />
            <Field
              label="Confirmar nova senha"
              type="password"
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              required
              error={naoConfere ? 'As senhas não coincidem.' : undefined}
            />
            {erro && (
              <p className="auth-error" role="alert">
                {erro}
              </p>
            )}
            <div className="fin__ctas">
              <button
                type="button"
                className="nv-btn nv-btn--secondary"
                onClick={() => {
                  setAberta(null);
                  setTrocandoSenha(false);
                  setErro(null);
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="nv-btn nv-btn--primary" disabled={!podeTrocar || enviando}>
                {enviando ? 'Alterando…' : 'Salvar senha'}
              </button>
            </div>
          </form>
        </Sheet>
      )}
    </section>
  );
}
